"use client";
import { useTranslations } from "next-intl";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import {
  Controller,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from "react-hook-form";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Spinner } from "../spinner";

// ----------------------------------------------------------------------

type SelectOption = {
  label: ReactNode;
  value: string;
};

interface CustomRHFSelectProps<TFieldValues extends FieldValues>
  extends Omit<
      ComponentPropsWithoutRef<typeof Controller>,
      "name" | "control" | "render"
    >,
    Omit<ComponentPropsWithoutRef<typeof Select>, "name" | "defaultValue"> {
  name: FieldPath<TFieldValues>;
  helperText?: string;
  containerClass?: string;
  options: SelectOption[];
  emptyText?: string;
  selectContentClassName?: string;
  selectItemClassName?: string;
  triggerClassName?: string;
  placeholder?: string;
  loading?: boolean;
}

export default function CustomRHFSelect<TFieldValues extends FieldValues>({
  name,
  helperText,
  defaultValue,
  rules,
  containerClass,
  options,
  emptyText,
  selectContentClassName,
  selectItemClassName,
  triggerClassName,
  placeholder,
  loading = false,
  disabled = false,
  onValueChange,
  ...other
}: CustomRHFSelectProps<TFieldValues>) {
  const { control } = useFormContext();
  const t = useTranslations("common.form");

  const handleValueChange = (
    value: string,
    onChange: (value: string) => void,
  ) => {
    if (value !== "empty") {
      onChange(value);
      onValueChange?.(value);
      return;
    }
    onChange("");
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        return (
          <div className={cn("space-y-2 w-full", containerClass)}>
            <Select
              onValueChange={(value) =>
                handleValueChange(value, field.onChange)
              }
              value={field.value || ""}
              disabled={loading || disabled}
              {...other}
            >
              <SelectTrigger
                className={cn(
                  "w-full",
                  error && "border-semantic-error",
                  triggerClassName,
                )}
                size="default"
                aria-invalid={error ? true : undefined}
                disabled={disabled || loading}
              >
                {loading ? (
                  <Spinner className="h-4 w-4 text-text-secondary ms-auto" />
                ) : (
                  <SelectValue
                    placeholder={placeholder || emptyText || t("select")}
                  />
                )}
              </SelectTrigger>
              <SelectContent
                className={cn(
                  "bg-bg-secondary rounded-sm border-stroke-subtle shadow-[0px_2px_4px_-2px_#0000001A,0px_4px_6px_-1px_#0000001A]",
                  selectContentClassName,
                )}
              >
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Spinner className="h-5 w-5 text-text-secondary" />
                  </div>
                ) : (
                  <>
                    {emptyText && (
                      <SelectItem
                        value="empty"
                        className={cn(
                          "rounded-sm focus:bg-bg-tertiary/60 outline-none",
                          selectItemClassName,
                        )}
                      >
                        <span className="text-primary">{emptyText}</span>
                      </SelectItem>
                    )}
                    {options.map((o, idx) => (
                      <SelectItem
                        key={`${o.value}-${idx}`}
                        value={o.value}
                        className={cn(
                          "font-medium rounded-sm",
                          selectItemClassName,
                        )}
                      >
                        {o.label}
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
            {!!helperText && (
              <p className="text-xs leading-tight text-muted-foreground ml-1.5">
                {helperText}
              </p>
            )}
            {!!error && (
              <p className="text-xs leading-tight text-semantic-error ml-1.5">
                {error?.message}
              </p>
            )}
          </div>
        );
      }}
    />
  );
}
